import { ExportFormat, triggerBrowserDownload, toJson, toExcelXml, toCsv, toAsync, streamToBlob, streamNdjson, streamJsonArray, streamCsv, resolveColumns, formatCell, chunk, buildExportFilename, EXPORT_MIME, EXPORT_EXTENSION, DEFAULT_CSV_DELIMITER, CSV_BOM } from '@authe/fe-commons/export';
export { CSV_BOM, DEFAULT_CSV_DELIMITER, EXPORT_EXTENSION, EXPORT_MIME, ExportFormat, buildExportFilename, chunk, formatCell, resolveColumns, streamCsv, streamJsonArray, streamNdjson, streamToBlob, toAsync, toCsv, toExcelXml, toJson, triggerBrowserDownload } from '@authe/fe-commons/export';
import { createLogger } from '@authe/fe-commons/logging';
import { TX_FIELDS } from '@authe/fe-commons/display';
import { unwrapData } from '@authe/fe-commons/api';
import * as React2 from 'react';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { DropdownMenu, DropdownMenuTrigger, Button, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem, notifyError } from '@authe/ui-core';
import * as share_star from '@authe/fe-commons/share';

var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __getOwnPropNames = Object.getOwnPropertyNames;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};
var __copyProps = (to, from, except, desc) => {
  if (from && typeof from === "object" || typeof from === "function") {
    for (let key of __getOwnPropNames(from))
      if (!__hasOwnProp.call(to, key) && key !== except)
        __defProp(to, key, { get: () => from[key], enumerable: !(desc = __getOwnPropDesc(from, key)) || desc.enumerable });
  }
  return to;
};
var __reExport = (target, mod, secondTarget) => (__copyProps(target, mod, "default"), secondTarget);

// src/index.ts
var src_exports = {};
__export(src_exports, {
  CSV_BOM: () => CSV_BOM,
  DEFAULT_CSV_DELIMITER: () => DEFAULT_CSV_DELIMITER,
  DEFAULT_FILENAME_BASE: () => DEFAULT_FILENAME_BASE,
  DEFAULT_TX_COLUMNS: () => DEFAULT_TX_COLUMNS,
  EXPORTS_PATH: () => EXPORTS_PATH,
  EXPORT_EXTENSION: () => EXPORT_EXTENSION,
  EXPORT_MIME: () => EXPORT_MIME,
  EXPORT_PACKAGE: () => EXPORT_PACKAGE,
  ExportButton: () => ExportButton,
  ExportFormat: () => ExportFormat,
  ExportHistoryItem: () => ExportHistoryItem,
  ExportLedgerProvider: () => ExportLedgerProvider,
  ExportLedgerStatus: () => ExportLedgerStatus,
  ExportMenu: () => ExportMenu,
  PDF_HEADER_HEIGHT: () => PDF_HEADER_HEIGHT,
  PDF_MARGIN: () => PDF_MARGIN,
  buildExportFilename: () => buildExportFilename,
  chunk: () => chunk,
  createApiExportLedger: () => createApiExportLedger,
  exportDataset: () => exportDataset,
  exportTransactionsCsv: () => exportTransactionsCsv,
  formatCell: () => formatCell,
  ledgerContentType: () => ledgerContentType,
  log: () => log,
  resolveColumns: () => resolveColumns,
  streamCsv: () => streamCsv,
  streamJsonArray: () => streamJsonArray,
  streamNdjson: () => streamNdjson,
  streamToBlob: () => streamToBlob,
  toAsync: () => toAsync,
  toCsv: () => toCsv,
  toExcelXml: () => toExcelXml,
  toJson: () => toJson,
  triggerBrowserDownload: () => triggerBrowserDownload,
  useExport: () => useExport,
  useExportLedger: () => useExportLedger
});
var EXPORT_PACKAGE = "export";
var log = createLogger(`fe:${EXPORT_PACKAGE}`);
var DEFAULT_FILENAME_BASE = "authe-export";
var PDF_MARGIN = 40;
var PDF_HEADER_HEIGHT = 64;
function requireColumns(columns, format) {
  if (!columns || columns.length === 0) {
    throw new Error(`[export] "${format}" export requires \`columns\``);
  }
  return columns;
}
function counting(source, tally) {
  return (async function* count() {
    for await (const row of toAsync(source)) {
      tally.rows += 1;
      yield row;
    }
  })();
}
async function encode(opts, tally) {
  const { columns, format } = opts;
  const rows = counting(opts.rows, tally);
  switch (format) {
    case ExportFormat.CSV: {
      const blob = await streamToBlob(
        streamCsv(rows, requireColumns(columns, format), { bom: true, ...opts.csv }),
        EXPORT_MIME.csv
      );
      return { blob, mime: EXPORT_MIME.csv };
    }
    case ExportFormat.NDJSON: {
      const blob = await streamToBlob(streamNdjson(rows, { columns }), EXPORT_MIME.ndjson);
      return { blob, mime: EXPORT_MIME.ndjson };
    }
    case ExportFormat.JSON: {
      const text = await toJson(rows, { columns, indent: 2 });
      return { blob: new Blob([text], { type: EXPORT_MIME.json }), mime: EXPORT_MIME.json };
    }
    case ExportFormat.EXCEL: {
      const cols = requireColumns(columns, format);
      const buffered = [];
      for await (const row of rows) buffered.push(row);
      const xml = toExcelXml(buffered, cols, opts.excel);
      return { blob: new Blob([xml], { type: EXPORT_MIME.excel }), mime: EXPORT_MIME.excel };
    }
    default: {
      const exhaustive = format;
      throw new Error(`[export] unsupported format: ${String(exhaustive)}`);
    }
  }
}
async function exportDataset(opts) {
  const tally = { rows: 0 };
  const { blob } = await encode(opts, tally);
  const filename = buildExportFilename(opts.filenameBase ?? DEFAULT_FILENAME_BASE, opts.format, {
    timestamp: opts.timestamp ?? true
  });
  const downloaded = opts.download === false ? false : triggerBrowserDownload(blob, filename);
  log.debug(`exported ${filename} (${tally.rows} rows, ${blob.size} bytes, downloaded=${downloaded})`);
  return {
    blob,
    filename,
    downloaded,
    rowCount: tally.rows,
    columns: opts.columns ? resolveColumns(opts.columns).map((c) => c.header) : []
  };
}
var DEFAULT_TX_COLUMNS = [
  TX_FIELDS.txHash,
  TX_FIELDS.chainId,
  TX_FIELDS.blockNumber,
  TX_FIELDS.blockTimestamp,
  TX_FIELDS.type,
  TX_FIELDS.finality,
  TX_FIELDS.totalValueRaw,
  // Beside the value, because an exported "0" for a Monero transfer is only
  // honest next to the column that says the amount was hidden.
  TX_FIELDS.amountHidden,
  TX_FIELDS.valueUsd
];
function exportTransactionsCsv(rows, opts = {}) {
  return exportDataset({
    rows,
    columns: opts.columns ?? DEFAULT_TX_COLUMNS,
    format: opts.format ?? "csv",
    filenameBase: opts.filenameBase ?? "transactions",
    timestamp: opts.timestamp,
    download: opts.download
  });
}

// src/ledger/types.ts
var ExportLedgerStatus = {
  /** Encoded + downloaded client-side; the ledger kept metadata only. */
  RECORDED: "recorded",
  /** A stored copy was requested; the upload slot was issued, bytes unconfirmed. */
  PENDING: "pending",
  /** Bytes confirmed in S3 and re-downloadable through a presigned URL. */
  STORED: "stored",
  /** The export produced no file. `error` says why. */
  FAILED: "failed"
};
var EXPORTS_PATH = "/exports";
var ledgerLog = log.child("ledger");
function entryFromWire(raw) {
  return {
    id: String(raw.id ?? ""),
    resource: raw.resource ?? "",
    tableId: raw.table_id ?? void 0,
    viewId: raw.view_id ?? void 0,
    filters: raw.filters ?? void 0,
    format: raw.export_format ?? "csv",
    filename: raw.filename ?? "",
    rowCount: Number(raw.row_count ?? 0),
    byteSize: Number(raw.byte_size ?? 0),
    columns: Array.isArray(raw.columns) ? raw.columns.map(String) : [],
    status: raw.status ?? ExportLedgerStatus.RECORDED,
    error: raw.error ?? void 0,
    createdAt: raw.created_at ? Number(raw.created_at) * 1e3 : void 0,
    createdBy: raw.created_by ?? null,
    // Only present when bytes are held — see `ExportLedgerEntry.downloadUrl`.
    downloadUrl: raw.download_url ?? void 0
  };
}
function createApiExportLedger(client, options = {}) {
  const base = options.basePath ?? EXPORTS_PATH;
  const upload = async (slot, blob) => {
    if (!slot.url) throw new Error("the ledger issued no upload URL");
    const response = await fetch(slot.url, {
      method: slot.method ?? "PUT",
      // The content type is signed INTO the URL; sending a different one is
      // rejected by storage, which is what keeps the slot single-purpose.
      headers: slot.content_type ? { "Content-Type": slot.content_type } : void 0,
      body: blob
    });
    if (!response.ok) {
      throw new Error(`upload failed (${response.status})`);
    }
  };
  const settle = async (id, body) => entryFromWire(unwrapData(await client.patch(`${base}/${encodeURIComponent(id)}`, body)));
  return {
    async record(input) {
      const store = Boolean(input.store && input.blob);
      try {
        const created = unwrapData(
          await client.post(base, {
            resource: input.resource,
            table_id: input.tableId,
            view_id: input.viewId,
            filters: input.filters,
            export_format: input.format,
            filename: input.filename,
            row_count: input.rowCount ?? 0,
            byte_size: input.blob?.size ?? 0,
            columns: input.columns ? [...input.columns] : [],
            status: input.status,
            error: input.error,
            store
          })
        );
        const entry = entryFromWire(created);
        if (!store) return entry;
        try {
          await upload(created.upload ?? {}, input.blob);
        } catch (err) {
          ledgerLog.warn(`export upload failed: ${String(err)}`);
          return await settle(entry.id, {
            status: ExportLedgerStatus.FAILED,
            error: err instanceof Error ? err.message : String(err)
          }).catch(() => entry);
        }
        return await settle(entry.id, { status: ExportLedgerStatus.STORED });
      } catch (err) {
        ledgerLog.warn(`export was not recorded: ${String(err)}`);
        return null;
      }
    },
    async list(query = {}) {
      const params = new URLSearchParams();
      if (query.viewId) params.set("view_id", query.viewId);
      if (query.tenantWide) params.set("scope", "tenant");
      if (query.limit) params.set("limit", String(query.limit));
      if (query.cursor) params.set("cursor", query.cursor);
      const suffix = params.toString();
      const body = unwrapData(
        await client.get(
          suffix ? `${base}?${suffix}` : base
        )
      );
      return {
        entries: (body.items ?? []).map(entryFromWire),
        nextCursor: body.next_cursor ?? void 0
      };
    },
    async get(id) {
      return entryFromWire(
        unwrapData(await client.get(`${base}/${encodeURIComponent(id)}`))
      );
    },
    async remove(id) {
      unwrapData(await client.delete(`${base}/${encodeURIComponent(id)}`));
    }
  };
}
function ledgerContentType(format) {
  return EXPORT_MIME[format] ?? "application/octet-stream";
}
var LedgerContext = React2.createContext(null);
function ExportLedgerProvider({
  ledger,
  children
}) {
  return /* @__PURE__ */ jsx(LedgerContext.Provider, { value: ledger, children });
}
function useExportLedger() {
  return React2.useContext(LedgerContext);
}
function useExport(options = {}) {
  const ambient = useExportLedger();
  const ledger = options.ledger === void 0 ? ambient : options.ledger;
  const [exporting, setExporting] = React2.useState(false);
  const [error, setError] = React2.useState(null);
  const [lastResult, setLastResult] = React2.useState(null);
  const mounted = React2.useRef(true);
  const onErrorRef = React2.useRef(options.onError);
  const recordRef = React2.useRef({
    ledger,
    context: options.context,
    store: options.store
  });
  React2.useEffect(() => {
    onErrorRef.current = options.onError;
    recordRef.current = { ledger, context: options.context, store: options.store };
  });
  React2.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const run = React2.useCallback(async (opts) => {
    setExporting(true);
    setError(null);
    try {
      const result = await exportDataset(opts);
      if (mounted.current) setLastResult(result);
      void recordExport(recordRef.current, opts.format, result);
      return result;
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      log.error(`export failed: ${e.message}`);
      if (mounted.current) setError(e);
      onErrorRef.current?.(e);
      void recordFailure(recordRef.current, opts.format, e);
      return null;
    } finally {
      if (mounted.current) setExporting(false);
    }
  }, []);
  const reset = React2.useCallback(() => {
    setError(null);
    setLastResult(null);
  }, []);
  return { exporting, error, lastResult, run, reset };
}
async function recordExport(recording, format, result) {
  const { ledger, context, store } = recording;
  if (!ledger || !context) return;
  try {
    await ledger.record({
      ...context,
      format,
      filename: result.filename,
      rowCount: result.rowCount,
      columns: result.columns,
      status: ExportLedgerStatus.RECORDED,
      store,
      blob: store ? result.blob : void 0
    });
  } catch (err) {
    log.warn(`export was not recorded: ${String(err)}`);
  }
}
async function recordFailure(recording, format, error) {
  const { ledger, context } = recording;
  if (!ledger || !context) return;
  try {
    await ledger.record({
      ...context,
      format,
      filename: "",
      status: ExportLedgerStatus.FAILED,
      error: error.message
    });
  } catch (err) {
    log.warn(`failed export was not recorded: ${String(err)}`);
  }
}
var FORMAT_LABELS = {
  [ExportFormat.CSV]: "CSV",
  [ExportFormat.EXCEL]: "Excel (.xls)",
  [ExportFormat.JSON]: "JSON",
  [ExportFormat.NDJSON]: "JSON Lines"
};
function useExportAction({
  rows,
  columns,
  filenameBase,
  onError,
  exportContext,
  store
}) {
  const report = React2.useCallback(
    (error) => {
      if (onError) onError(error);
      else notifyError(error, { title: "Couldn't export this data." });
    },
    [onError]
  );
  const { exporting, run } = useExport({ onError: report, context: exportContext, store });
  const rowsRef = React2.useRef(rows);
  React2.useEffect(() => {
    rowsRef.current = rows;
  });
  const handle = React2.useCallback(
    async (format) => {
      const source = rowsRef.current;
      let resolved;
      try {
        resolved = typeof source === "function" ? await source() : source;
      } catch (err) {
        report(err instanceof Error ? err : new Error(String(err)));
        return;
      }
      await run({ rows: resolved, columns, format, filenameBase });
    },
    [columns, filenameBase, run, report]
  );
  return { exporting, run: handle };
}
var DEFAULT_FORMATS = [
  ExportFormat.CSV,
  ExportFormat.EXCEL,
  ExportFormat.JSON
];
function ExportMenu({
  rows,
  columns,
  formats = DEFAULT_FORMATS,
  filenameBase,
  label = "Export",
  buttonVariant = "outline",
  buttonSize = "sm",
  disabled,
  exportContext,
  store,
  historyHref,
  onError
}) {
  const { exporting, run } = useExportAction({
    rows,
    columns,
    filenameBase,
    onError,
    exportContext,
    store
  });
  return /* @__PURE__ */ jsxs(DropdownMenu, { children: [
    /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { variant: buttonVariant, size: buttonSize, disabled: disabled || exporting, children: exporting ? "Exporting\u2026" : label }) }),
    /* @__PURE__ */ jsxs(DropdownMenuContent, { align: "end", children: [
      /* @__PURE__ */ jsx(DropdownMenuLabel, { children: "Download as" }),
      /* @__PURE__ */ jsx(DropdownMenuSeparator, {}),
      formats.map((format) => /* @__PURE__ */ jsx(
        DropdownMenuItem,
        {
          disabled: exporting,
          onSelect: () => void run(format),
          children: FORMAT_LABELS[format]
        },
        format
      )),
      /* @__PURE__ */ jsx(ExportHistoryItem, { href: historyHref })
    ] })
  ] });
}
function ExportHistoryItem({ href }) {
  if (!href) return null;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(DropdownMenuSeparator, {}),
    /* @__PURE__ */ jsx(DropdownMenuItem, { asChild: true, children: /* @__PURE__ */ jsx("a", { href, children: "Your exports" }) })
  ] });
}
function ExportButton({
  rows,
  columns,
  format = ExportFormat.CSV,
  filenameBase,
  label = "Export",
  variant = "outline",
  size = "sm",
  disabled,
  exportContext,
  store,
  onError
}) {
  const { exporting, run } = useExportAction({
    rows,
    columns,
    filenameBase,
    onError,
    exportContext,
    store
  });
  return /* @__PURE__ */ jsx(
    Button,
    {
      variant,
      size,
      disabled: disabled || exporting,
      onClick: () => void run(format),
      children: exporting ? "Exporting\u2026" : label
    }
  );
}

// src/share/index.ts
var share_exports = {};

// src/share/createShareableLink.ts
var createShareableLink_exports = {};
__reExport(createShareableLink_exports, share_star);

// src/share/index.ts
__reExport(share_exports, createShareableLink_exports);

// src/index.ts
__reExport(src_exports, share_exports);

export { DEFAULT_FILENAME_BASE, DEFAULT_TX_COLUMNS, EXPORTS_PATH, EXPORT_PACKAGE, ExportButton, ExportHistoryItem, ExportLedgerProvider, ExportLedgerStatus, ExportMenu, PDF_HEADER_HEIGHT, PDF_MARGIN, createApiExportLedger, exportDataset, exportTransactionsCsv, ledgerContentType, log, useExport, useExportLedger };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map